import { all, fork, put, takeEvery, call, select } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/taxInvoice/lookupActionType';
import * as actionCreators from 'appRedux/actions/taxInvoice/lookupActions';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { AppState } from 'appRedux/reducers/ReducerState';

// API endpoint mapping
const LOOKUP_API_ENDPOINTS: Record<actionTypes.LookupType, string> = {
  country_code: 'pms/rpc/salestax/get_country_code',
  document_pattern: 'pms/rpc/salestax/get_document_pattern',
  itemizer: 'pms/rpc/salestax/get_itemizer',
  money_text: 'pms/rpc/salestax/get_money_text',
  tax_book: 'pms/rpc/salestax/get_tax_book',
  tax_buyer_type: 'pms/rpc/salestax/get_tax_buyer_type',
  tax_profile_list: 'pms/rpc/salestax/get_tax_profile_list',
};

// Static data to avoid API rate limit – use instead of calling get_tax_buyer_type
const STATIC_TAX_BUYER_TYPE_RESPONSE: actionTypes.LookupResponse = {
  data: {
    result_code: '0',
    result_msg: 'Success',
    msg_code: '00',
    result_data: [
      { code: 'CCPT', name: 'Foreigner' },
      { code: 'NIDN', name: 'Individual' },
      { code: 'OTHR', name: 'Not provider information' },
      { code: 'TXID', name: 'Corporation' },
    ],
  },
  status: 200,
  message: 'OK',
};

// Static data to avoid API rate limit – use instead of calling get_money_text
const STATIC_MONEY_TEXT_RESPONSE: actionTypes.LookupResponse = {
  data: {
    result_code: '0',
    result_msg: 'Success',
    msg_code: '00',
    result_data: [
      { id: 1, code: 'EN', name: 'English' },
      { id: 2, code: 'TH', name: 'Thai' },
    ],
  },
  status: 200,
  message: 'OK',
};

// Lookups that must not call API (rate limit): return empty data only (get_tax_book is still called)
const SKIP_API_CALL_LOOKUPS: actionTypes.LookupType[] = [
  'country_code',
  'document_pattern',
  'itemizer',
];

function* lookupRequestProcess({ payload }: actionTypes.LookupRequestAction): Generator<any, void, any> {
  try {
    const { lookupType, request } = payload;

    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    
    const salestaxValue = systemConfig?.dataSource?.interfaces?.SALESTAX;
    const isSalesTaxEnabled = salestaxValue === true;

    if (!isSalesTaxEnabled) {
      console.log(`Lookup API (${lookupType}): SALESTAX is disabled (value: ${salestaxValue}), skipping API call`);
      yield put(actionCreators.lookupSuccess(lookupType, {
        data: {
          result_code: '0',
          result_msg: 'SALESTAX is disabled',
          msg_code: '00',
          result_data: []
        },
        status: 200,
        message: 'OK'
      }));
      return;
    }

    // Use static data instead of API to avoid rate limit
    if (lookupType === 'tax_buyer_type') {
      yield put(actionCreators.lookupSuccess('tax_buyer_type', STATIC_TAX_BUYER_TYPE_RESPONSE));
      return;
    }
    if (lookupType === 'money_text') {
      yield put(actionCreators.lookupSuccess('money_text', STATIC_MONEY_TEXT_RESPONSE));
      return;
    }

    // Skip API call for these lookups (rate limit) – return empty success
    if (SKIP_API_CALL_LOOKUPS.includes(lookupType)) {
      yield put(actionCreators.lookupSuccess(lookupType, {
        data: {
          result_code: '0',
          result_msg: 'Success',
          msg_code: '00',
          result_data: []
        },
        status: 200,
        message: 'OK'
      }));
      return;
    }

    // Only tax_profile_list reaches here – call API (intended to be used on click only, not on initial load)
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error(`Lookup API (${lookupType}): Access token is missing`);
      yield put(actionCreators.lookupError(lookupType, 'Authentication token is missing. Please login again.'));
      return;
    }

    const endpoint = LOOKUP_API_ENDPOINTS[lookupType];
    const url = `${API_URL_V1}${endpoint}`;

    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: any = yield call(APIPostRequest, url, request, API_PMS_HEADERS);

    // APIPostRequest return response.data โดยตรง
    // Response structure จาก server: { data: { result_code, result_msg, result_data }, status, message }
    // แต่ APIPostRequest return response.data โดยตรง ดังนั้น response = { data: {...}, status, message }

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const hasResultData = responseData?.result_data !== undefined;
    const resultCode = responseData?.result_code;
    // Support both structures: result_data as array or result_data.items
    const resultData = responseData?.result_data || {};
    const items = Array.isArray(resultData) ? resultData : (resultData?.items || []);

    // ตรวจสอบว่า response มี status หรือไม่
    // ถ้ามี status และเป็น 200 หรือไม่มี status แต่มี result_code = '0' แสดงว่าสำเร็จ
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode && hasResultData) {
      // สร้าง LookupResponse structure ที่ตรงกับ type definition
      // Keep the original result_data structure (may contain items and paging)
      const lookupResponse: actionTypes.LookupResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code || '00',
          result_data: resultData, // Keep original structure (object with items/paging or array)
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.lookupSuccess(lookupType, lookupResponse));
    } else {
      // Error case
      const errorMessage = responseData?.result_msg || response?.message || `Failed to fetch ${lookupType}`;

      yield put(actionCreators.lookupError(lookupType, errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized or already logged out
        const responseDataForError = response?.data || response;
        const errorMsg = responseDataForError?.result_msg || response?.message || errorMessage;
        const errorData = responseDataForError?.data;

        const isAlreadyLoggedOut =
          errorMsg === 'Already logged out' ||
          errorMsg === 'Invalid token, authentication required' ||
          errorData === null ||
          errorData === undefined;

        // Signout if already logged out or if data exists (unauthorized)
        if (isAlreadyLoggedOut || errorData) {
          yield put(userSignOut());
        }
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.lookupError(payload.lookupType, errorMessage, error));
  }
}

function* lookupRequest() {
  yield takeEvery(actionTypes.LOOKUP_REQUEST, lookupRequestProcess);
}

export default function* rootSaga() {
  yield all([fork(lookupRequest)]);
}

